import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { rupee } from "../lib/api";
import { VegBadge } from "../components/VegBadge";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, linePrice, subtotal } = useCart();
  const navigate = useNavigate();
  const tax = Math.round((subtotal) * 0.05);
  const delivery = subtotal >= 999 || subtotal === 0 ? 0 : 40;
  const total = subtotal + tax + delivery;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="mx-auto text-gold mb-4" size={42} />
        <h1 className="font-serif text-3xl text-navy">Your cart is empty</h1>
        <p className="text-muted-foreground mt-2">Add some royal dishes to begin your feast.</p>
        <Link to="/menu" className="inline-flex mt-6 bg-navy text-cream font-semibold px-7 py-3 rounded-full hover:bg-navy-soft transition-colors">Explore Menu</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="cart-page">
      <h1 className="font-serif text-4xl text-navy mb-6">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.lineId} className="bg-white rounded-xl border border-black/5 p-4 flex gap-4" data-testid={`cart-item-${it.slug}`}>
              <img src={it.image} alt={it.name} className="w-20 h-20 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <VegBadge veg={it.veg} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-navy leading-tight">{it.name}</h3>
                    {it.variant && <div className="text-xs text-muted-foreground">{it.variant}</div>}
                    {it.customizations?.length > 0 && <div className="text-xs text-muted-foreground mt-0.5">+ {it.customizations.map((c) => c.name).join(", ")}</div>}
                    {it.add_ons?.length > 0 && <div className="text-xs text-muted-foreground">Add-ons: {it.add_ons.map((c) => c.name).join(", ")}</div>}
                  </div>
                  <button onClick={() => removeItem(it.lineId)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-${it.slug}`}><Trash2 size={16} /></button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-black/15 rounded-full">
                    <button onClick={() => updateQty(it.lineId, -1)} className="p-2" data-testid={`cart-minus-${it.slug}`}><Minus size={14} /></button>
                    <span className="w-7 text-center text-sm font-semibold">{it.quantity}</span>
                    <button onClick={() => updateQty(it.lineId, 1)} className="p-2" data-testid={`cart-plus-${it.slug}`}><Plus size={14} /></button>
                  </div>
                  <span className="font-semibold text-navy">{rupee(linePrice(it))}</span>
                </div>
              </div>
            </div>
          ))}
          <Link to="/menu" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold">Continue Shopping</Link>
        </div>

        <div className="bg-white rounded-xl border border-black/5 p-6 h-fit lg:sticky lg:top-24" data-testid="cart-summary">
          <h3 className="font-serif text-2xl text-navy mb-4">Order Summary</h3>
          <div className="space-y-2.5 text-sm">
            <Row label="Subtotal" value={rupee(subtotal)} testid="summary-subtotal" />
            <Row label="GST (5%)" value={rupee(tax)} />
            <Row label="Delivery Fee" value={delivery === 0 ? "Free" : rupee(delivery)} />
          </div>
          <div className="gold-rule my-4" />
          <div className="flex justify-between font-semibold text-lg text-navy"><span>Grand Total</span><span data-testid="summary-total">{rupee(total)}</span></div>
          <button onClick={() => navigate("/checkout")} data-testid="proceed-checkout-btn" className="w-full mt-5 btn-shine bg-gold text-navy font-semibold py-3.5 rounded-full hover:bg-gold-light transition-colors flex items-center justify-center gap-2">Proceed to Checkout <ArrowRight size={17} /></button>
          <p className="text-xs text-muted-foreground text-center mt-3">Apply coupons at checkout · Free delivery over ₹999</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return <div className="flex justify-between text-muted-foreground"><span>{label}</span><span className="text-navy font-medium" data-testid={testid}>{value}</span></div>;
}
